import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState, useEffect } from 'react';
import {
  Box,
  InputLabel,
  TextField,
  Button,
  Divider,
  Typography,
  Select,
  MenuItem,
  useTheme,
  SelectChangeEvent
} from '@mui/material';
import {
  CreateDepartmentBody,
  DepartmentExtended,
  deptBusiness
} from '../../../../../shared/types/Department';
import { UserSearch } from '../../KnockoutScreens/Followup/FollowupTypeDropDown';
import {
  useCreateDepartmentMutation,
  useEditDepartmentMutation,
  useGetUserDisplayNamesQuery
} from '../../../../services/departmentSlice/departmentSlice';
import DepartmentIconRenderer, { DEPARTMENT_ICON_MAP } from '../../../../assets/svg/departmentIconMap';

const textFieldSx = {
  color: '#222222',
  '& > p': {
    margin: '5px 0px',
    color: '#646669',
    fontSize: typographyTokens.fontSizes.xs,
  },
};

const AddDepartment = ({
  onClose,
  value,
  showUser,
  setShowUser
}: {
  onClose: () => void;
  value: DepartmentExtended | undefined;
  showUser: any
  setShowUser: any
}) => {
  const [originValue, setOriginValue] = useState<CreateDepartmentBody>({
    name: '',
    adAppRole: '',
    contactID: '',
    assignmentType: '',
    stakeholderVariableId: ''
  });
  
  const [roundRobinWeightage, setRoundRobinWeightage ] = useState<{ id: string, displayName: string }[]>([])
  const [weightageList, setWeightageList ] = useState<any[]>([]);
  const [selectedIconName, setSelectedIconName] = useState<string>('');

  useEffect(() => {
    if (typeof value?.weightageList === 'string') {
      try {
        const parsed = JSON.parse(value.weightageList);
        setWeightageList(Array.isArray(parsed) ? parsed : []);
      } catch {
        setWeightageList([]);
      }
    }  else {
      setWeightageList([]);
    }
  }, [value]);

  const options: any = [
    {id: 1, label: "Default"},
    {id: 2, label: "Round Robin"},
    {id: 3, label: deptBusiness?.basedOnBusinessUnit}
  ]

  useEffect(() => {
    setOriginValue({
      name: value ? value.name : '',
      adAppRole: value ? value.adAppRole : '',
      assignmentType: value ? value.assignmentType : '',
      contactID:  value?.contactID,
      stakeholderVariableId: value ? value.stakeholderVariableId || '' : ''
    });
    // Auto-select the icon when editing an existing department
    setSelectedIconName(value?.name && DEPARTMENT_ICON_MAP[value.name] ? value.name : '');
  }, [value]);

  useEffect(()=>{
    if(originValue){
    setRoundRobinWeightage(orderedUsers)
    }
  },[originValue])

  const clearForm = () => {
    setOriginValue({
      name: '',
      adAppRole: '',
      contactID: '',
      assignmentType: '',
      stakeholderVariableId: ''
    });
    setSelectedIconName('');
  };

  const [createDepartment] = useCreateDepartmentMutation();
  const [editDepartment] = useEditDepartmentMutation();
  const {data: userDetails} = useGetUserDisplayNamesQuery();
  const theme = useTheme();
  const valueArray: any = value?.contactID.split(',');
  const foundUsers = userDetails?.filter((user: any) => valueArray?.includes(user.id));

  const orderedUsers = valueArray?.map((id: any) => foundUsers?.find(user => user.id === id))
  const onUserChange = (user: string) => {
    setOriginValue((prevState) => ({ ...prevState, contactID: user }));
  };
   const contactIds = originValue && originValue.contactID ? originValue.contactID.split(',').map(id => id.trim()) : [];
    const filteredWeightageList = weightageList?.filter((item: any) =>
      contactIds.includes(item.id)
    );

  const handleAddDepartment = () => {
    createDepartment({data: originValue, weightageList: filteredWeightageList}).then((_) => clearForm());
    onClose();
  };

  const onMultipleUserChange = (users: string[]) => {
    setOriginValue((prevState) => ({
      ...prevState,
      contactID: users?.length > 1 ? users.join(',') : users[0] || '',
    }));
  };


  const handleEditDepartment = () => {
    editDepartment({
      data: originValue,
      id: value?.id || 0,
      weightageList: filteredWeightageList
    }).then((_) => clearForm());
    onClose();
  };

  const handleDepartmentNameChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    const newName = e.target.value;
    setOriginValue((prevState) => ({ ...prevState, name: newName }));
    // Auto-select icon if the typed name matches a mapped department
    if (DEPARTMENT_ICON_MAP[newName]) {
      setSelectedIconName(newName);
    } else {
      setSelectedIconName('');
    }
  };

  const handleAzureRoleChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    setOriginValue((prevState) => ({
      ...prevState,
      adAppRole: e.target.value,
    }));
  };
  const handleStakeholderVariableIdChange = (user: string) => {
    setOriginValue((prevState) => ({
      ...prevState,
      stakeholderVariableId: user,
    }));
  };

  const handleChange = (e: any) => {
    setOriginValue((prev) => ({...prev, assignmentType: e.target.value}))
  }

  const handleCancelClicked = () => {
    clearForm();
    onClose();
  };

  const isAddDepartment = value === undefined;
  const handleAcceptChangesClicked = isAddDepartment
    ? handleAddDepartment
    : handleEditDepartment;
  const isFormInvalid = originValue.name === '' || originValue.adAppRole === '';
  const disabled = isAddDepartment
    ? isFormInvalid
    : isFormInvalid ||
      (originValue.name === value.name &&
        originValue.adAppRole === value.adAppRole &&
        originValue.contactID === value.contactID);
  const acceptChangesText = isAddDepartment
    ? 'Add Department'
    : 'Edit Department';

  return (
    <>
      <Box sx={{ m: 3, overflowY: 'auto', overflowX: 'hidden', maxHeight: '70vh' }}>
        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222' }} htmlFor="department-name">
            Department Name
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="department-name"
            name="name"
            fullWidth
            value={originValue.name}
            placeholder="Ex. Department Architecture"
            onChange={handleDepartmentNameChange}
          />
        </Box>

        {/* Department Icon Selector */}
        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>
            Department Icon
          </InputLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            {/* <Select
              size="small"
              displayEmpty
              value={selectedIconName}
              onChange={(e: SelectChangeEvent<string>) => setSelectedIconName(e.target.value)}
              style={{ width: '300px' }}
              renderValue={(selected) =>
                selected ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DepartmentIconRenderer departmentName={selected} height={16} width={16} />
                    <span>{selected}</span>
                  </Box>
                ) : (
                  <span style={{ color: '#9e9e9e' }}>No icon assigned</span>
                )
              }
            >
              <MenuItem value="">
                <em>No icon assigned</em>
              </MenuItem>
              {Object.keys(DEPARTMENT_ICON_MAP).map((deptName) => (
                <MenuItem key={deptName} value={deptName}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DepartmentIconRenderer departmentName={deptName} height={16} width={16} />
                    <span>{deptName}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select> */}
            {/* Live preview of the selected icon */}
            {selectedIconName && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f8fc',
                }}
              >
                <DepartmentIconRenderer departmentName={selectedIconName} height={28} width={28} />
                <Typography sx={{ fontSize: typographyTokens.fontSizes.caption, color: theme.palette.mode === 'dark' ? '#ccc' : '#646669' }}>
                  {selectedIconName}
                </Typography>
              </Box>
            )}
          </Box>
          {!selectedIconName && originValue.name && (
            <Typography sx={{ fontSize: typographyTokens.fontSizes.xs, color: '#646669', mt: 0.5 }}>
              No icon mapped for &quot;{originValue.name}&quot;. Add one to departmentIconMap.tsx.
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }} htmlFor="azure-ad-role">
            Azure AD Role
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="azure-ad-role"
            name="name"
            fullWidth
            value={originValue.adAppRole}
            placeholder="Ex. AuthEnterpriseArchitecture"
            onChange={handleAzureRoleChange}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>
            Select Assignment Type
          </InputLabel>
          <Select
          size="small"
          id="demo-simple-select-helper"
          name="assignmentType"
          onChange={handleChange}
          value={originValue?.assignmentType ? originValue?.assignmentType : 'Default' }
          style={{marginBottom :'8px', width: '600px'}}
          >
            {options.map((val: any) => (
                <MenuItem key={val.id}
                value ={val.label}
                >
                {val.label}
                </MenuItem>
              ))}
          </Select>
        </Box>
        {originValue?.assignmentType === 'Round Robin' ? (
         <Box>
         <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222' }}>
             Round Robin Assignment (Click in box to add reviewer)
         </Box>
         <UserSearch
              adAppRole=""
              disabled={false}
              onChange={(users: any) => onMultipleUserChange(users)}
              existDefault={true}
              multiple={true}
              multipleUsers={orderedUsers}
              showUser={showUser}
              setShowUser={setShowUser}
              setWeightageList={setWeightageList}
              weightageListVal={ weightageList }
              showWeightageInput={true}
              // updatedUserList={updatedUsers}
          />
         </Box>
         ) : originValue?.assignmentType === deptBusiness?.basedOnBusinessUnit ? (
          <Box> By selecting this will be default Stakeholder based on requestor department
          </Box>
        ) : (
        <Box>
          <Box sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>
            Default Stakeholder for Department
          </Box>
          <UserSearch
            adAppRole=""
            disabled={false}
            onChange={onUserChange}
            existDefault={true}
            user={value?.contact?.displayName}
          />
        </Box>
        )}
        <Box sx={{ my: 3 }}>
          <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }} htmlFor="stakeholder-variable">
            Stakeholder (Task is assigned by a variable and no stakeholder was assigned)
          </InputLabel>
          <UserSearch
            adAppRole=""
            disabled={false}
            onChange={handleStakeholderVariableIdChange}
            existDefault={true}
            user={value?.stakeholderVariable?.displayName}
          />
        </Box>
      </Box>
      <Divider sx={{ backgroundColor: '#7E8083' }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'right',
          p: 2,
          '& > button': {
            m: 1,
          },
        }}
      >
        <Button
          variant="text"
          disabled={false}
          onClick={handleCancelClicked}
          sx={{ textTransform: 'none' }}
        >
          <Typography
            sx={{
              font: 'normal normal 600 16px/22px Open Sans',
              color: '#2372B9',
            }}
          >
            Cancel
          </Typography>
        </Button>
        <Button
          variant="contained"
          onClick={handleAcceptChangesClicked}
          sx={{
            textTransform: 'none',
            backgroundColor: '#2372B9',
            font: 'normal normal 600 16px/22px Open Sans',
          }}
        >
          {acceptChangesText}
        </Button>
      </Box>
    </>
  );
};

export default AddDepartment;
